/**
 * Route table + bidirectional resolver for the client-side router.
 *
 * PL and EN localized paths differ by translation (no /pl or /en prefix), which
 * means the path itself encodes the locale (`/uslugi` ⇒ pl, `/services` ⇒ en).
 * Matching therefore returns the locale too, so switching language = re-navigating
 * the same route key under the other locale's path.
 *
 * Hrefs are built via the FROZEN `resolveRoute` from @mb-group/graphql-client so
 * they stay identical to the gateway's sitemap/redirect handling; the local
 * `PATHS` mirror the same frozen spec and drive reverse-matching (+ query
 * strings, which resolveRoute does not handle). Keys NOT in the frozen set are
 * app-local and resolved from `PATHS` alone.
 */
import type { Locale } from "@mb-group/shared-types";
import { ROUTE_KEYS, resolveRoute } from "@mb-group/graphql-client";

/** Frozen route keys shared with the gateway, plus app-local routes. */
export type AppRouteKey =
	| "home"
	| "services"
	| "serviceDetail"
	| "companies"
	| "companyDetail"
	| "caseStudies"
	| "caseStudyDetail"
	| "knowledge"
	| "article"
	| "resources"
	| "webinars"
	| "landing"
	| "search"
	| "contact"
	| "privacy"
	| "cookies"
	// app-local (not in the shared ROUTE_KEYS):
	| "about"
	| "industries"
	| "formPage"
	| "preview"
	| "panel";

interface LocalizedPath {
	pl: string;
	en: string;
}

/**
 * Mirrors the frozen graphql-client route spec, extended with the two pages the
 * approved mock-up adds: "O Grupie" (about) and "Branże" (industries).
 */
const PATHS: Record<AppRouteKey, LocalizedPath> = {
	home: { pl: "/", en: "/" },
	services: { pl: "/uslugi", en: "/services" },
	serviceDetail: { pl: "/uslugi/:slug", en: "/services/:slug" },
	companies: { pl: "/spolki", en: "/companies" },
	companyDetail: { pl: "/spolki/:slug", en: "/companies/:slug" },
	caseStudies: { pl: "/realizacje", en: "/case-studies" },
	caseStudyDetail: { pl: "/realizacje/:slug", en: "/case-studies/:slug" },
	knowledge: { pl: "/baza-wiedzy", en: "/knowledge" },
	article: { pl: "/baza-wiedzy/:slug", en: "/knowledge/:slug" },
	resources: { pl: "/raporty", en: "/resources" },
	webinars: { pl: "/webinary", en: "/webinars" },
	landing: { pl: "/lp/:slug", en: "/lp/:slug" },
	search: { pl: "/szukaj", en: "/search" },
	contact: { pl: "/kontakt", en: "/contact" },
	privacy: { pl: "/prywatnosc", en: "/privacy" },
	cookies: { pl: "/cookies", en: "/cookies" },
	about: { pl: "/o-grupie", en: "/about-group" },
	industries: { pl: "/branze", en: "/industries" },
	formPage: { pl: "/formularz/:key", en: "/form/:key" },
	preview: { pl: "/podglad/:type/:slug", en: "/preview/:type/:slug" },
	panel: { pl: "/panel", en: "/panel" },
};

const ALL_KEYS = Object.keys(PATHS) as AppRouteKey[];
const FROZEN_KEYS = new Set<string>(ROUTE_KEYS);

export type RouteParams = Record<string, string>;
export type QueryParams = Record<string, string | undefined>;

export interface RouteMatch {
	key: AppRouteKey;
	locale: Locale;
	params: RouteParams;
	query: QueryParams;
}

function normalize(pathname: string): string {
	if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
	return pathname || "/";
}

function patternToRegex(pattern: string): { regex: RegExp; keys: string[] } {
	if (pattern === "/") return { regex: /^\/$/, keys: [] };
	const keys: string[] = [];
	const source = pattern
		.split("/")
		.filter(Boolean)
		.map((seg) => {
			if (seg.startsWith(":")) {
				keys.push(seg.slice(1));
				return "([^/]+)";
			}
			return seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		})
		.join("/");
	return { regex: new RegExp(`^/${source}$`), keys };
}

/** Parse a `?a=b&c=d` string into a plain object. */
export function parseQuery(search: string): QueryParams {
	const out: QueryParams = {};
	const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
	for (const [k, v] of params.entries()) out[k] = v;
	return out;
}

function buildQuery(query?: QueryParams): string {
	if (!query) return "";
	const params = new URLSearchParams();
	for (const [k, v] of Object.entries(query)) {
		if (v !== undefined && v !== "") params.set(k, v);
	}
	const s = params.toString();
	return s ? `?${s}` : "";
}

/**
 * Match a full location (pathname + search) to a route. Returns null for an
 * unknown path so the caller can render 404. `hint` disambiguates locale for the
 * shared `/` root.
 */
export function matchLocation(pathname: string, search: string, hint: Locale): RouteMatch | null {
	const path = normalize(pathname);
	const query = parseQuery(search);
	const candidates: RouteMatch[] = [];

	for (const key of ALL_KEYS) {
		for (const locale of ["pl", "en"] as const) {
			const { regex, keys } = patternToRegex(PATHS[key][locale]);
			const m = regex.exec(path);
			if (!m) continue;
			const params: RouteParams = {};
			keys.forEach((name, i) => {
				const value = m[i + 1];
				if (value !== undefined) params[name] = decodeURIComponent(value);
			});
			candidates.push({ key, locale, params, query });
		}
	}

	if (candidates.length === 0) return null;
	// Prefer a candidate matching the locale hint (only `/` is genuinely ambiguous).
	return candidates.find((c) => c.locale === hint) ?? candidates[0] ?? null;
}

/** Build an href for a route key in a locale, appending an optional query. */
export function buildHref(
	key: AppRouteKey,
	locale: Locale,
	params?: RouteParams,
	query?: QueryParams,
): string {
	let base: string;
	if (FROZEN_KEYS.has(key)) {
		try {
			base = params ? resolveRoute(key, { locale, params }) : resolveRoute(key, { locale });
		} catch {
			base = fillPattern(PATHS[key][locale], params);
		}
	} else {
		base = fillPattern(PATHS[key][locale], params);
	}
	return base + buildQuery(query);
}

function fillPattern(pattern: string, params?: RouteParams): string {
	if (!params) return pattern;
	return pattern.replace(/:([A-Za-z0-9_]+)/g, (_, name: string) =>
		params[name] !== undefined ? encodeURIComponent(params[name]) : `:${name}`,
	);
}

/** The localized path template for a key (used for locale switching UI). */
export function pathTemplate(key: AppRouteKey, locale: Locale): string {
	return PATHS[key][locale];
}

export { ALL_KEYS };
